import { groupEquipment, homeSelection } from "@repo/client/onboarding";
import type { EquipmentFilter } from "@repo/contract/catalog";
import {
  "profile-editors.equipment_helper" as equipmentHelper,
  "equipment.group.accessories" as groupAccessories,
  "equipment.group.body_weight" as groupBodyWeight,
  "equipment.group.free_weights" as groupFreeWeights,
  "equipment.group.machines" as groupMachines,
  "equipment.quick.full_gym" as quickFullGym,
  "equipment.quick.home" as quickHome,
} from "@repo/i18n";
import { useCallback } from "react";
import { Button, MultiChips } from "../../../ui/controls";
import { FieldError, FieldLabel, Helper } from "./parts";

const GROUP_LABEL: Readonly<Record<string, () => string>> = {
  accessories: groupAccessories,
  body_weight: groupBodyWeight,
  free_weights: groupFreeWeights,
  machines: groupMachines,
};

export interface EquipmentFormProps {
  error?: string;
  onChange: (value: number[]) => void;
  /** `/catalog/filters`' equipment rows: names from the catalogue, localized
   *  there, never from i18n (`D26`). */
  rows: readonly EquipmentFilter[];
  value: readonly number[];
}

/**
 * The checklist (A2 `MultiSheet` for "Com o que você pode treinar?"): the
 * catalogue's rows in the contract's four groups, plus the two quick fills.
 * It edits `available` only — never the place (see `checklistInput`).
 */
export function EquipmentForm({
  rows,
  value,
  onChange,
  error,
}: EquipmentFormProps) {
  const fillHome = useCallback(
    () => onChange([...homeSelection()]),
    [onChange]
  );
  const fillGym = useCallback(
    () => onChange(rows.map((row) => row.id)),
    [onChange, rows]
  );
  return (
    <div>
      <Helper>{equipmentHelper()}</Helper>
      <div className="mb-6 flex flex-wrap gap-2">
        <Button onClick={fillHome} variant="secondary">
          {quickHome()}
        </Button>
        <Button onClick={fillGym} variant="secondary">
          {quickFullGym()}
        </Button>
      </div>
      <div className="flex flex-col gap-6">
        {groupEquipment(rows).map((group) => (
          <EquipmentGroup
            key={group.group}
            label={GROUP_LABEL[group.group]?.() ?? group.group}
            onChange={onChange}
            rows={group.rows}
            value={value}
          />
        ))}
      </div>
      {error ? <FieldError>{error}</FieldError> : null}
    </div>
  );
}

function EquipmentGroup({
  label,
  rows,
  value,
  onChange,
}: {
  label: string;
  onChange: (value: number[]) => void;
  rows: readonly EquipmentFilter[];
  value: readonly number[];
}) {
  const ids = new Set(rows.map((row) => row.id));
  // The group edits its own ids; every other group's ticks ride along.
  const change = useCallback(
    (next: string[]) =>
      onChange([...value.filter((id) => !ids.has(id)), ...next.map(Number)]),
    [onChange, value, ids]
  );
  return (
    <section>
      <FieldLabel>{label}</FieldLabel>
      <MultiChips
        label={label}
        onValueChange={change}
        options={rows.map((row) => ({
          label: row.name,
          value: String(row.id),
        }))}
        value={value.filter((id) => ids.has(id)).map(String)}
      />
    </section>
  );
}
